import React, { useCallback, useMemo, useState } from "react";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import {
  WORKSPACE_INVITE_ROLE_LABELS,
  WORKSPACE_INVITE_ROLE_OPTIONS,
} from "../../../services/workspaces/workspaceMembers.helpers";
import type { WorkspaceInviteRole } from "../../../services/workspaces/workspaceMembers.types";
import { Dropdown } from "../Dropdown";
import type { WorkspaceInviteRoleSelectProps } from "./WorkspaceInviteRoleSelect.types";

const ROLE_OPTIONS = WORKSPACE_INVITE_ROLE_OPTIONS.map((value) => ({
  value,
  label: WORKSPACE_INVITE_ROLE_LABELS[value],
}));

export const WorkspaceInviteRoleSelect: React.FC<
  WorkspaceInviteRoleSelectProps
> = ({
  value,
  onChange,
  disabled = false,
  ariaLabel = "Member role",
  presentation = "default",
  chevronSize =
    presentation === "inline" ? 20 : presentation === "chip" ? 14 : 16,
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const { triggerRef, menuRef, rect } = usePortalMenu<
    HTMLButtonElement,
    HTMLUListElement
  >({
    open,
    onOutsideClick: close,
  });

  const handleSelect = useCallback(
    (role: WorkspaceInviteRole) => {
      onChange(role);
      close();
    },
    [close, onChange],
  );

  const options = useMemo(() => ROLE_OPTIONS, []);

  return (
    <Dropdown
      value={value}
      options={options}
      open={open}
      onToggle={() => !disabled && setOpen((prev) => !prev)}
      onSelect={handleSelect}
      triggerRef={triggerRef}
      menuRef={menuRef}
      menuRect={rect}
      disabled={disabled}
      compact
      variant={
        presentation === "chip"
          ? "chip"
          : presentation === "inline"
            ? "inline"
            : "ghost"
      }
      fullWidth={false}
      chevronSize={chevronSize}
      ariaLabel={ariaLabel}
      minMenuWidth={120}
    />
  );
};

export default WorkspaceInviteRoleSelect;
